import { Edit, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DataListCard, type DataListColumn, type DataListAction } from '@/components/common/DataListCard';
import type { InsuranceCompany } from '../types';

interface InsuranceCompanyListProps {
  companies: InsuranceCompany[];
  onEdit: (company: InsuranceCompany) => void;
  onDeactivate: (company: InsuranceCompany) => void;
  isLoading?: boolean;
  canEdit?: boolean;
}

export function InsuranceCompanyList({
  companies,
  onEdit,
  onDeactivate,
  isLoading = false,
  canEdit = false,
}: InsuranceCompanyListProps) {
  const columns: DataListColumn<InsuranceCompany>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (company) => company.name,
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (company) => company.phone || '-',
    },
    {
      key: 'email',
      label: 'Email',
      render: (company) => company.email || '-',
    },
    {
      key: 'adjustors',
      label: 'Adjustors',
      render: (company) => company.adjustorCount,
    },
    {
      key: 'status',
      label: 'Status',
      render: (company) => (
        <Badge variant={company.isActive ? 'default' : 'secondary'}>
          {company.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  const actions: DataListAction<InsuranceCompany>[] = [
    {
      label: 'View',
      href: (company) => `/insurance/${company.id}`,
    },
    {
      label: '',
      icon: <Edit className="h-4 w-4" />,
      onClick: onEdit,
      title: 'Edit company',
      canShow: () => canEdit,
    },
    {
      label: '',
      icon: <Trash2 className="h-4 w-4 text-red-500" />,
      onClick: onDeactivate,
      canShow: (company) => canEdit && company.isActive,
      title: 'Deactivate company',
    },
  ];

  return (
    <DataListCard
      items={companies}
      columns={columns}
      actions={actions}
      keyExtractor={(company) => company.id}
      isLoading={isLoading}
      loadingMessage="Loading companies..."
      emptyMessage="No insurance companies found."
    />
  );
}
